/**
 * Storm history: what the public record says about hail at a property.
 *
 * A rep presses one button on a job and gets a document an insurance adjuster
 * will read. That document is only worth having if a hostile reader can check
 * every line of it, so the shape of what this module produces is fixed by three
 * rules, and none of them are cosmetic:
 *
 *   1. **Every tabulated row is independently verifiable.** Each event carries a
 *      reference marker (S1a, S1b…) pointing at a date-scoped query URL that
 *      returns that exact record, plus its UTC timestamp of record, so a row on
 *      our page maps to a line in somebody else's download. Contractor storm
 *      reports generally do not do this, and it is the main reason this one
 *      reads as evidence rather than marketing.
 *   2. **Measured and estimated are never blurred.** `qualifier` comes from the
 *      source record untouched. A stone somebody physically measured is the
 *      strongest thing in public records; a guess from a passer-by is not, and
 *      must never borrow its authority.
 *   3. **Derived numbers are labelled as derived.** Distance and bearing are not
 *      in the source data — we compute them. They are the two columns an
 *      adjuster is most likely to challenge, so the document states the method,
 *      the origin, the Earth radius and the precision limit. See
 *      `stormReportHtml.ts`.
 *
 * The temptation with this kind of document is to make the storm look closer and
 * bigger than it was. An adjuster who catches one overstatement discounts the
 * whole page, so nothing here rounds in our favour: distances round to a tenth
 * of a mile in the ordinary way, sub-threshold reports are counted and disclosed
 * rather than dropped silently, and an empty result is a real answer that gets
 * rendered as one.
 *
 * Source is the NWS Local Storm Report archive served by the Iowa Environmental
 * Mesonet. Radar-derived hail (SWDI `nx3hail`) is deliberately not mixed in: it
 * is a probability estimate on a 1 km grid, it runs large against measured
 * stones, and putting it in the same table as ground observations would hand a
 * carrier an easy reason to dismiss the lot. See ADR 0043.
 *
 * ## Wind is not hail with different units
 *
 * The report covers wind as well, and the two perils behave so differently that
 * treating them alike would produce a document that is quietly wrong.
 *
 *   * **Most wind reports have no measurement at all.** `MAG` arrives as the
 *     literal string `"None"`. The Weather Service logs a wind damage report
 *     because something broke, not because anybody held an anemometer — so the
 *     observation *is* the damage, and a threshold in miles per hour would
 *     discard the entire body of evidence. For the reference property, 130 of
 *     139 wind reports inside five miles carried no speed, and not one measured
 *     gust reached the 58 mph severe criterion in ten years.
 *   * **Wind reports are far more numerous.** Those same five miles hold 139
 *     wind reports across 66 separate days, against 13 hail reports across six.
 *     Six wind-damage days a year is a fact a carrier can turn around — "wind
 *     damage is routine on this street" — so the wind radius is tighter than the
 *     hail radius by default, and both are printed on the document's face.
 *
 * Hence two radii, two kinds of measurement, and a `peril` on every row.
 */

/**
 * Every event type the report tabulates.
 *
 * `TSTM` is a thunderstorm; the `NON-TSTM` pair are the same damage and gusts
 * from gradient or straight-line wind, which flattens a roof just as well and
 * belongs to the same claim. `HIGH WIND` is the sustained-wind product and is
 * asked for even though it is rare inland, because a type that is absent costs
 * nothing and a type that is missing loses evidence silently.
 */
export const LSR_TYPES = [
  "HAIL",
  "TSTM WND GST",
  "TSTM WND DMG",
  "NON-TSTM WND GST",
  "NON-TSTM WND DMG",
  "HIGH WIND",
] as const;

export const LSR_ENDPOINT =
  "https://mesonet.agron.iastate.edu/cgi-bin/request/gis/lsr.py";

/** Mean Earth radius, in miles. Stated in the document's derivation section. */
export const EARTH_RADIUS_MILES = 3958.8;

const COMPASS = [
  "N",
  "NNE",
  "NE",
  "ENE",
  "E",
  "ESE",
  "SE",
  "SSE",
  "S",
  "SSW",
  "SW",
  "WSW",
  "W",
  "WNW",
  "NW",
  "NNW",
] as const;

export type Compass = (typeof COMPASS)[number];

/**
 * What was reported.
 *
 * `wind-damage` is a report with no wind speed on it — the observation is that
 * something broke. `wind-gust` is a report carrying a measured or estimated
 * speed in miles per hour. They are never merged: a row saying a roof came off
 * must not be presented as though somebody clocked the wind that did it.
 */
export type Peril = "hail" | "wind-damage" | "wind-gust";

/**
 * The severe thunderstorm wind criterion, in miles per hour.
 *
 * 58 mph is 50 knots, which is where the Weather Service draws severe. Gusts
 * below it are not tabulated: the archive is full of mesonet stations logging
 * 11 to 37 mph, and a 13 mph gust on a claim document is noise that invites a
 * reader to discount the rows around it. Damage reports are not filtered by
 * this and never could be — they carry no speed.
 */
export const SEVERE_GUST_MPH = 58;

/** What the rep chose before the pull. Never adjusted afterwards — see below. */
export interface StormSearch {
  /** How far back to look, in years. */
  years: number;
  /** The radius a hail report must fall inside, in miles. */
  radiusMiles: number;
  /** The smallest hail diameter that earns a row, in inches. */
  thresholdInches: number;
  /**
   * The radius a wind report must fall inside, in miles.
   *
   * Separate from the hail radius, and smaller by default, for the reason set
   * out at the top of this file: wind reports are several times more numerous,
   * and a page of them argues that wind damage is ordinary in this
   * neighbourhood rather than that a storm hit this roof.
   */
  windRadiusMiles: number;
}

/**
 * Ten years, five miles, three-quarters of an inch.
 *
 * The threshold is not the current NWS severe criterion, which is 1.00 inch. It
 * is 0.75 because that is roughly where an argument about asphalt shingle damage
 * starts, and because 0.75 was the severe criterion before 2010, so older rows
 * in the archive cluster there. Whichever value is used ends up stated in the
 * document — it is never left implicit.
 */
export const STORM_SEARCH_DEFAULTS: StormSearch = {
  years: 10,
  radiusMiles: 5,
  thresholdInches: 0.75,
  windRadiusMiles: 2,
};

/**
 * The limits a search is held to.
 *
 * A rep can widen the search, which is the point of letting them set it — a
 * tight pull sometimes comes back empty on a property that plainly took hail.
 * What they cannot do is widen it until something appears: the radius and the
 * threshold used are printed in the scope paragraph and in the derivation
 * section, so a fifty-mile pull announces itself to the adjuster. The bounds
 * here only stop a typo turning into a query that scans half a state.
 */
export const STORM_SEARCH_LIMITS = {
  years: { min: 1, max: 30 },
  radiusMiles: { min: 1, max: 25 },
  thresholdInches: { min: 0.25, max: 4 },
  windRadiusMiles: { min: 1, max: 25 },
} as const;

export interface StormSearchProblem {
  field: keyof StormSearch;
  message: string;
}

/**
 * Check a search before it is sent anywhere.
 *
 * Shared by the modal and the Edge Function. The function does not trust the
 * modal — the modal is JavaScript on somebody else's laptop, and the URL it
 * produces is fetched by our server.
 */
export function validateStormSearch(search: StormSearch): StormSearchProblem[] {
  const problems: StormSearchProblem[] = [];
  const check = (
    field: keyof StormSearch,
    label: string,
    { min, max }: { min: number; max: number },
    unit: string,
  ) => {
    const value = search[field];
    if (!Number.isFinite(value)) {
      problems.push({ field, message: `${label} must be a number.` });
    } else if (value < min || value > max) {
      problems.push({
        field,
        message: `${label} must be between ${min} and ${max} ${unit}.`,
      });
    }
  };

  check("years", "Years searched", STORM_SEARCH_LIMITS.years, "years");
  check("radiusMiles", "Hail radius", STORM_SEARCH_LIMITS.radiusMiles, "miles");
  check(
    "thresholdInches",
    "Hail size",
    STORM_SEARCH_LIMITS.thresholdInches,
    "inches",
  );
  check(
    "windRadiusMiles",
    "Wind radius",
    STORM_SEARCH_LIMITS.windRadiusMiles,
    "miles",
  );
  return problems;
}

/** Where the property is. `lng` rather than `lon`, matching `jobs`. */
export interface Origin {
  lat: number;
  lng: number;
}

/** The address as it should read on the document. */
export interface StormAddress {
  /** "3387 Brookview Trace, Hoover, AL 35216" — the masthead and the footer. */
  full: string;
  /** The county, if we know it. Shown in the identification strip. */
  county: string | null;
}

export interface BoundingBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** One row of the source CSV, before we have decided anything about it. */
export type LsrRow = Record<string, string>;

/** One qualifying report, with the derived columns worked out. */
export interface StormEvent {
  /** What was reported. Decides which of the two measurements is filled. */
  peril: Peril;
  /** The UTC timestamp of record, "YYYY/MM/DD HH:MM", exactly as served. */
  utc: string;
  /** Hail diameter in inches. Null on every wind row. */
  diameterInches: number | null;
  /** Wind speed in miles per hour. Null on hail, and on damage reports. */
  gustMph: number | null;
  /** Straight from the source record. Never inferred, never upgraded. */
  qualifier: "Measured" | "Estimated" | "Unknown";
  /** Derived — see M1 in the document. */
  miles: number;
  bearing: Compass;
  bearingDeg: number;
  city: string;
  county: string;
  state: string;
  /** Who called it in: "Trained Spotter", "NWS Employee", "Public". */
  source: string;
  /** NWS Weather Forecast Office that issued it, e.g. "BMX". */
  wfo: string;
  /**
   * What the reporting party said.
   *
   * Carried through and shown for wind rows, where it is the whole observation:
   * "shingles off roof of homes" is the evidence, and there is no number to put
   * in its place. Hail rows have a diameter doing that job, so their remark is
   * kept for the record and not printed.
   */
  remark: string;
  lat: number;
  lng: number;
  /** The reference marker tying this row to a date-scoped query. */
  ref: string;
}

/** A date-scoped query that returns one day's reports, for checking a row. */
export interface StormVerification {
  /** "2023-03-24". */
  day: string;
  ref: string;
  url: string;
  rowsReturned: number;
  rowsWithinRadius: number;
}

/**
 * Everything the document is rendered from, and everything it is checked
 * against later.
 *
 * Frozen when the report is pulled. The reasoning is ADR 0032's, one layer
 * along: a presented document is frozen because "what did you send me on
 * Tuesday?" must have an answer, and a storm report that has gone to a carrier
 * is exactly that question with more at stake. Local Storm Reports are
 * preliminary and get revised in quality control, so re-running the same query
 * next year can legitimately return different rows — which is precisely why the
 * copy we sent has to keep saying what it said.
 */
export interface StormReportSnapshot {
  address: StormAddress;
  origin: Origin;
  search: StormSearch;
  /** ISO date the pull happened, "2026-08-18". */
  retrievedOn: string;
  /** Period reviewed, ISO dates. */
  periodStart: string;
  periodEnd: string;
  bbox: BoundingBox;
  /** The query every row in the report ultimately comes from. */
  masterUrl: string;
  counts: {
    /** Rows that earned a place in the table, both perils together. */
    events: number;
    hail: number;
    wind: number;
    /**
     * Hail inside the hail radius but under the threshold. Disclosed in the
     * summary rather than dropped, so a reader can tell a quiet area from a
     * filtered one. There is no wind equivalent — a damage report has no
     * measurement to be under anything.
     */
    subThreshold: number;
    /** Everything the master query returned, before any filtering. */
    rawRows: number;
  };
  events: StormEvent[];
  verification: StormVerification[];
}

const toRadians = (deg: number): number => (deg * Math.PI) / 180;
const toDegrees = (rad: number): number => (rad * 180) / Math.PI;

/**
 * Great-circle distance in miles.
 *
 * Source coordinates are rounded to two decimal degrees — about half a mile —
 * so a distance out of here carries roughly ±0.7 miles of uncertainty however
 * many decimals it is printed to. The document says so; do not quietly present
 * these as exact.
 */
export function haversineMiles(from: Origin, to: Origin): number {
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) *
      Math.cos(toRadians(to.lat)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(a));
}

/** Initial great-circle bearing from the property to the report. */
export function bearingFrom(
  from: Origin,
  to: Origin,
): { compass: Compass; degrees: number } {
  const dLng = toRadians(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(toRadians(to.lat));
  const x =
    Math.cos(toRadians(from.lat)) * Math.sin(toRadians(to.lat)) -
    Math.sin(toRadians(from.lat)) *
      Math.cos(toRadians(to.lat)) *
      Math.cos(dLng);
  const degrees = (toDegrees(Math.atan2(y, x)) + 360) % 360;
  const compass = COMPASS[Math.round(degrees / 22.5) % 16] ?? "N";
  return { compass, degrees };
}

/**
 * A box to ask the service for, padded well past the radius.
 *
 * The endpoint filters by bounding box and we filter to the true circle
 * afterwards, so the padding costs a few extra rows and buys certainty that
 * nothing inside the radius was cut off by a corner.
 */
export function boundingBox(origin: Origin, radiusMiles: number): BoundingBox {
  const padded = radiusMiles * 1.6;
  const dLat = padded / 69;
  const dLng = padded / (69 * Math.max(Math.cos(toRadians(origin.lat)), 0.01));
  const round = (n: number) => Math.round(n * 10000) / 10000;
  return {
    north: round(origin.lat + dLat),
    south: round(origin.lat - dLat),
    east: round(origin.lng + dLng),
    west: round(origin.lng - dLng),
  };
}

/** Build one query URL. The only shape of URL this feature ever fetches. */
export function lsrQueryUrl(
  bbox: BoundingBox,
  startUtc: string,
  endUtc: string,
): string {
  const params = new URLSearchParams({
    sts: startUtc,
    ets: endUtc,
    type: LSR_TYPES.join(","),
    fmt: "csv",
    justcsv: "1",
    north: String(bbox.north),
    south: String(bbox.south),
    east: String(bbox.east),
    west: String(bbox.west),
  });
  return `${LSR_ENDPOINT}?${params.toString()}`;
}

/** "2026-08-18" → "2026-08-18T00:00Z", the format the endpoint wants. */
export const asQueryInstant = (isoDate: string): string => `${isoDate}T00:00Z`;

/** The day after an ISO date, without dragging in a date library. */
export function nextDay(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** `years` back from an ISO date, used for the start of the review period. */
export function yearsBefore(isoDate: string, years: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() - years);
  return d.toISOString().slice(0, 10);
}

/**
 * Parse the served CSV.
 *
 * Written out rather than split on commas because `REMARK` is free text a
 * spotter typed, and it contains commas and quotation marks as a matter of
 * course. A naive split shifts every column after the remark by one, which
 * shows up as a hail diameter appearing in the county column — wrong in a way
 * that is easy to miss on a page that otherwise looks right.
 */
export function parseLsrCsv(text: string): LsrRow[] {
  // A byte order mark, written as an escape: as a literal character it is
  // invisible in the source and lint refuses it outright.
  const trimmed = text.replace(/^\uFEFF/, "").trim();
  if (!trimmed) return [];

  const records: string[][] = [];
  let field = "";
  let record: string[] = [];
  let quoted = false;

  for (let i = 0; i < trimmed.length; i += 1) {
    const ch = trimmed[i];
    if (quoted) {
      if (ch === '"') {
        if (trimmed[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      record.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      // A CRLF is one break, not two, and a trailing break is not a row.
      if (ch === "\r" && trimmed[i + 1] === "\n") i += 1;
      record.push(field);
      records.push(record);
      field = "";
      record = [];
    } else {
      field += ch;
    }
  }
  record.push(field);
  records.push(record);

  const [header, ...rest] = records;
  if (!header) return [];
  const keys = header.map((k) => k.trim().toUpperCase());
  return rest
    .filter((cells) => cells.some((c) => c.trim() !== ""))
    .map((cells) => {
      const row: LsrRow = {};
      keys.forEach((key, idx) => {
        row[key] = (cells[idx] ?? "").trim();
      });
      return row;
    });
}

/**
 * Title-case a shouted field.
 *
 * Rows from before about 2020 arrive in capitals. Presentation only — this is
 * never let near a magnitude, a date or a qualifier.
 */
export function softenCaps(value: string): string {
  if (!value) return "";
  if (value !== value.toUpperCase()) return value;
  return value
    .toLowerCase()
    .replace(
      /(^|[\s/(-])([a-z])/g,
      (_, lead: string, ch: string) => lead + ch.toUpperCase(),
    );
}

/**
 * Which peril a row is, from its `TYPETEXT`.
 *
 * Matched on the suffix rather than the whole string so that the thunderstorm
 * and non-thunderstorm variants of each land together, and so a type the
 * service adds later fails loudly here rather than being silently tabulated as
 * something it is not.
 */
export function perilOf(typeText: string): Peril | null {
  const type = typeText.trim().toUpperCase();
  if (type === "HAIL") return "hail";
  if (type.endsWith("WND DMG")) return "wind-damage";
  if (type.endsWith("WND GST") || type === "HIGH WIND") return "wind-gust";
  return null;
}

/**
 * A magnitude, when there is one.
 *
 * The archive writes the literal string `"None"` where a report has no
 * measurement, not an empty field — so `Number()` alone yields `NaN` and a
 * naive check reads it as a broken row and drops it. Those rows are the wind
 * damage reports, which is to say most of the wind evidence in the file.
 */
export function magnitudeOf(raw: string | undefined): number | null {
  const value = (raw ?? "").trim();
  if (!value || value.toLowerCase() === "none") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const QUALIFIERS: Record<string, StormEvent["qualifier"]> = {
  M: "Measured",
  E: "Estimated",
};

/** The key a duplicate shares. See `dedupeRows`. */
const rowIdentity = (row: LsrRow): string =>
  [
    row.VALID2 ?? "",
    row.LAT ?? "",
    row.LON ?? "",
    row.MAG ?? "",
    // Without the type, hail and wind damage reported at the same minute from
    // the same place — which is exactly what a severe storm produces — would
    // collapse into one row, and the one that survived would be whichever the
    // service happened to serve first.
    row.TYPETEXT ?? "",
  ].join("|");

/**
 * Drop the same report arriving twice.
 *
 * A location on a county line is served once per county, identical but for the
 * `COUNTY` field. Counting both inflates the number in the summary paragraph,
 * which is the one number an adjuster checks first.
 */
export function dedupeRows(rows: LsrRow[]): LsrRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = rowIdentity(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** "2023/03/24 21:30" → "2023-03-24". */
export const dayOf = (utc: string): string =>
  utc.slice(0, 10).replace(/\//g, "-");

export interface StormPullInput {
  origin: Origin;
  address: StormAddress;
  search: StormSearch;
  /** ISO date to treat as today. Passed in so the result is reproducible. */
  today: string;
  /** The master query's CSV, as served. */
  masterCsv: string;
  /**
   * Each distinct event day's CSV, keyed by "YYYY-MM-DD". Absent days simply
   * lose their row counts; the query URL is still cited, because the URL is what
   * makes the row checkable and it is built rather than fetched.
   */
  dayCsv?: Record<string, string> | undefined;
}

/**
 * Turn what the service returned into the thing the document is rendered from.
 *
 * Pure, so the golden tests can hold it to a fixed CSV, and so the same
 * function runs in the browser and in a test without a network anywhere near
 * it.
 */
/** The wider of the two radii — what the service actually has to be asked for. */
export const searchRadius = (search: StormSearch): number =>
  Math.max(search.radiusMiles, search.windRadiusMiles);

export function buildStormReport(input: StormPullInput): StormReportSnapshot {
  const { origin, address, search, today, masterCsv } = input;
  const periodEnd = today;
  const periodStart = yearsBefore(today, search.years);
  // One query serves both perils, so the box has to cover whichever radius is
  // wider; each row is then held to its own.
  const bbox = boundingBox(origin, searchRadius(search));
  const masterUrl = lsrQueryUrl(
    bbox,
    asQueryInstant(periodStart),
    asQueryInstant(periodEnd),
  );

  const rows = dedupeRows(parseLsrCsv(masterCsv));

  const events: StormEvent[] = [];
  let subThreshold = 0;

  for (const row of rows) {
    const lat = Number(row.LAT);
    const lng = Number(row.LON);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;

    const peril = perilOf(row.TYPETEXT ?? "");
    if (!peril) continue;

    const magnitude = magnitudeOf(row.MAG);
    const miles = haversineMiles(origin, { lat, lng });

    // Two radii, because the two perils are not comparable in volume. See the
    // note at the top of this file.
    const radius =
      peril === "hail" ? search.radiusMiles : search.windRadiusMiles;
    if (miles > radius) continue;

    let diameterInches: number | null = null;
    let gustMph: number | null = null;

    if (peril === "hail") {
      // A hail row with no readable diameter cannot be placed above or below
      // the threshold, so it is not tabulated — and not counted as
      // sub-threshold either, because it is not evidence of a size at all.
      if (magnitude === null) continue;
      if (magnitude < search.thresholdInches) {
        subThreshold += 1;
        continue;
      }
      diameterInches = magnitude;
    } else if (peril === "wind-gust") {
      // Only at or above the severe criterion. The archive is full of mesonet
      // stations logging ordinary breezes, and an 11 mph gust on a claim
      // document invites a reader to discount the rows around it.
      if (magnitude === null || magnitude < SEVERE_GUST_MPH) continue;
      gustMph = magnitude;
    }
    // wind-damage falls through with neither measurement, which is correct:
    // the report exists because something broke, and that is the observation.

    const { compass, degrees } = bearingFrom(origin, { lat, lng });
    events.push({
      peril,
      utc: row.VALID2 ?? "",
      diameterInches,
      gustMph,
      qualifier:
        QUALIFIERS[(row.QUALIFIER ?? "").trim().toUpperCase()] ?? "Unknown",
      miles: Math.round(miles * 10) / 10,
      bearing: compass,
      bearingDeg: Math.round(degrees * 10) / 10,
      city: softenCaps(row.CITY ?? ""),
      county: softenCaps(row.COUNTY ?? ""),
      state: (row.STATE ?? "").trim(),
      source: softenCaps(row.SOURCE ?? ""),
      wfo: (row.WFO ?? "").trim(),
      remark: (row.REMARK ?? "").trim(),
      lat,
      lng,
      ref: "",
    });
  }

  // Newest first: the primary date of loss is the thing the reader came for.
  events.sort((a, b) => b.utc.localeCompare(a.utc));

  // One reference marker per distinct day, lettered from the newest. Every row
  // on that day shares it, because they share the query that returns them.
  const days = [...new Set(events.map((e) => dayOf(e.utc)))].sort().reverse();
  const verification: StormVerification[] = days.map((day, index) => {
    const url = lsrQueryUrl(
      bbox,
      asQueryInstant(day),
      asQueryInstant(nextDay(day)),
    );
    const csv = input.dayCsv?.[day];
    const returned = csv ? dedupeRows(parseLsrCsv(csv)) : [];
    // Counted against this report's own criteria rather than one radius: with
    // two perils and two radii, "within five miles" would be true of some rows
    // and meaningless for others.
    const withinRadius = returned.filter((row) => {
      const lat = Number(row.LAT);
      const lng = Number(row.LON);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
      const peril = perilOf(row.TYPETEXT ?? "");
      if (!peril) return false;
      const radius =
        peril === "hail" ? search.radiusMiles : search.windRadiusMiles;
      return haversineMiles(origin, { lat, lng }) <= radius;
    }).length;
    return {
      day,
      ref: `S1${refLetter(index)}`,
      url,
      rowsReturned: returned.length,
      rowsWithinRadius: withinRadius,
    };
  });

  const refByDay = new Map(verification.map((v) => [v.day, v.ref]));
  for (const event of events) {
    event.ref = refByDay.get(dayOf(event.utc)) ?? "S1";
  }

  return {
    address,
    origin,
    search,
    retrievedOn: today,
    periodStart,
    periodEnd,
    bbox,
    masterUrl,
    counts: {
      // `events` is the total and keeps its name: storm_reports.event_count is
      // a generated column reading snapshot->'counts'->>'events'.
      events: events.length,
      hail: events.filter((e) => e.peril === "hail").length,
      wind: events.filter((e) => e.peril !== "hail").length,
      subThreshold,
      rawRows: rows.length,
    },
    events,
    verification,
  };
}

/**
 * a, b, c … z, then aa, ab, so a very wide pull cannot collide two days onto
 * one marker. Twenty-six was enough for the reference report and is not enough
 * for a thirty-year search.
 */
export function refLetter(index: number): string {
  let n = index;
  let out = "";
  do {
    out = String.fromCharCode(97 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

/** The days a second round of queries should fetch, given a master pull. */
export function eventDays(snapshot: StormReportSnapshot): string[] {
  return snapshot.verification.map((v) => v.day);
}

/**
 * How strong a single row is as evidence, as a tier.
 *
 * Quantified beats unquantified, and measured beats estimated. A stone somebody
 * physically measured is the best thing the archive holds; a damage report is
 * the weakest of the four only in the sense that it carries no number — it is
 * still an official record that something broke, which is why it is tabulated
 * at all.
 */
export function evidenceTier(event: StormEvent): number {
  if (event.peril === "hail") return event.qualifier === "Measured" ? 4 : 3;
  if (event.peril === "wind-gust") return 2;
  return 1;
}

/** The number the tier is then broken by, in that tier's own unit. */
const tierMagnitude = (event: StormEvent): number =>
  event.diameterInches ?? event.gustMph ?? 0;

/**
 * Every event sharing the newest date, and the one that leads the callout.
 *
 * Strongest evidence first, then largest, then nearest. A measured stone leads
 * over a bigger estimate reported the same day from closer in, and hail leads
 * over wind on a day that produced both — because a diameter is a harder fact
 * than "a tree came down".
 */
export function primaryDateOfLoss(
  events: readonly StormEvent[],
): { day: string; sameDay: StormEvent[]; lead: StormEvent } | null {
  const newest = events[0];
  if (!newest) return null;
  const day = dayOf(newest.utc);
  const sameDay = events.filter((e) => dayOf(e.utc) === day);
  const lead = sameDay.reduce<StormEvent>((best, e) => {
    const rank = (x: StormEvent): [number, number, number] => [
      evidenceTier(x),
      tierMagnitude(x),
      -x.miles,
    ];
    const [at, am, an] = rank(best);
    const [bt, bm, bn] = rank(e);
    if (bt !== at) return bt > at ? e : best;
    if (bm !== am) return bm > am ? e : best;
    return bn > an ? e : best;
  }, newest);
  return { day, sameDay, lead };
}

/** How a row's measurement reads on the page: "1.75 in", "58 mph", or nothing. */
export function measurementLabel(event: StormEvent): string | null {
  if (event.diameterInches !== null)
    return `${event.diameterInches.toFixed(2)} in`;
  if (event.gustMph !== null) return `${Math.round(event.gustMph)} mph`;
  return null;
}

/** What a row is called on the page. */
export const PERIL_LABEL: Record<Peril, string> = {
  hail: "Hail",
  "wind-damage": "Wind damage",
  "wind-gust": "Wind gust",
};

/**
 * The strongest report on any day other than the primary one.
 *
 * Not simply the next date back, which is what this used to be. Once wind
 * joined the report that rule started burying the evidence: wind damage reports
 * are frequent and often trivial — a tree down, one location — so the two most
 * recent days are commonly a pair of them, and a callout naming only those
 * would leave a significant hail day further down the page unmentioned. An
 * adjuster who reads the callout and stops has then read the weakest thing in
 * the document.
 *
 * So the second line names the heaviest event in the period instead, ranked the
 * same way the primary day's lead is. It is labelled as such on the page rather
 * than presented as a date of loss, because it is not one.
 */
export function mostSignificantOtherDate(
  events: readonly StormEvent[],
): StormEvent | null {
  const primary = primaryDateOfLoss(events);
  if (!primary) return null;

  const others = events.filter((e) => dayOf(e.utc) !== primary.day);
  const best = others.reduce<StormEvent | null>((winner, e) => {
    if (!winner) return e;
    const rank = (x: StormEvent): [number, number, number] => [
      evidenceTier(x),
      tierMagnitude(x),
      -x.miles,
    ];
    const [at, am, an] = rank(winner);
    const [bt, bm, bn] = rank(e);
    if (bt !== at) return bt > at ? e : winner;
    if (bm !== am) return bm > am ? e : winner;
    return bn > an ? e : winner;
  }, null);

  return best;
}

/**
 * Days that produced four or more reports inside the radius.
 *
 * These get their own note. Worth flagging to whoever is sending the report
 * rather than deciding for them: a heavy day years before the roof went on is
 * context, not damage, and it can cut against the claim on roof age.
 */
export function multiReportDays(
  events: readonly StormEvent[],
  minimum = 4,
): { day: string; group: StormEvent[] }[] {
  const byDay = new Map<string, StormEvent[]>();
  for (const event of events) {
    const day = dayOf(event.utc);
    byDay.set(day, [...(byDay.get(day) ?? []), event]);
  }
  return [...byDay.entries()]
    .filter(([, group]) => group.length >= minimum)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([day, group]) => ({ day, group }));
}
